/**
 * THE single food/drinks/non-food decision for an order product line.
 *
 * Twin files: server/utils/spendSideUtils.ts <-> frontend/src/utils/spendSideUtils.ts — keep byte-identical.
 * NON_FOOD is `mainCategory` when present, else legacy `category` — this deliberately normalises the two
 * older idioms (`mainCategory === 'NON_FOOD' || category === 'NON_FOOD'` vs the ternary) onto one rule.
 * Spec: z_ralph/tasks/1_new_tasks/spec-combined-ordering-accounts.md §4
 *
 * - combinedOrderingAccount OFF (default): the ordering account's section type decides the whole
 *   order. A DRINKS-categorised product bought on a FOOD account is food cost (deliberate).
 * - combinedOrderingAccount ON: section type is ignored, the product's own mainCategory decides.
 *   A line with no mainCategory falls through to the section type (today's behaviour).
 * - NON_FOOD is product-level in BOTH modes.
 *
 * `null` = neither side (e.g. a NON_FOOD-section connection with the flag off) — callers building a
 * food/drinks split must drop it, exactly as they drop a non-matching section today.
 */
export type SpendSide = 'food' | 'drinks' | 'non-food' | null;

export function resolveSpendSide(
  product: { mainCategory?: string; category?: string },
  sectionType: string | undefined | null,
  combinedOrderingAccount: boolean,
): SpendSide {
  const isNonFood = product.mainCategory !== undefined
    ? product.mainCategory === 'NON_FOOD'
    : product.category === 'NON_FOOD';
  if (isNonFood) return 'non-food';

  if (combinedOrderingAccount) {
    if (product.mainCategory === 'DRINKS') return 'drinks';
    if (product.mainCategory === 'FOOD') return 'food';
    // no mainCategory — fall through to the section
  }

  if (sectionType === 'FOOD') return 'food';
  if (sectionType === 'DRINKS') return 'drinks';
  return null;
}

/**
 * `restaurant_platforms.settings.combinedOrderingAccount` reader. ABSENT KEY = OFF.
 * Only the super-admin toggle turns it on, per connection, by hand.
 */
export function isCombinedOrderingAccount(settings: unknown): boolean {
  if (!settings || typeof settings !== 'object') return false;
  return (settings as Record<string, unknown>).combinedOrderingAccount === true;
}
